import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building, User, Baby, ArrowRight, MapPin, Phone, Mail, GraduationCap, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/ukm9lnyj_SANS%20FOND%20V3.png';

export default function LoginModal({ isOpen, onClose, redirectTo = '/commande' }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [accountType, setAccountType] = useState('individual');
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', company: '', billing_address: '', contact_name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAccountType('individual');
      setForm({ email: '', password: '', name: '', phone: '', company: '', billing_address: '', contact_name: '' });
      setError('');
      setShowPassword(false);
      setForgotMode(false);
      setForgotSent(false);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    if (isOpen) {
      const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
      window.addEventListener('keydown', handleEsc);
      return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', handleEsc); };
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, onClose]);

  const handleIndividualSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/customers/login`, { email: form.email, password: form.password });
      localStorage.setItem('cg-customer', JSON.stringify({ id: data.id, name: data.name, email: data.email, phone: data.phone || '', type: 'individual', token: data.token }));
      onClose();
      navigate(redirectTo);
    } catch (err) {
      setError(err.response?.data?.detail || 'Courriel ou mot de passe incorrect');
    }
    setLoading(false);
  };

  const handleCorpoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        email: form.email,
        password: form.email,
        name: form.contact_name,
        phone: form.phone,
        type: 'corporate',
        company: form.company,
        billing_address: form.billing_address,
      };
      let data;
      try {
        const res = await axios.post(`${API}/api/customers/login`, { email: form.email, password: form.email });
        data = res.data;
      } catch {
        const res = await axios.post(`${API}/api/customers/register`, payload);
        data = res.data;
      }
      localStorage.setItem('cg-customer', JSON.stringify({ id: data.id, name: form.contact_name || data.name, email: form.email, phone: form.phone, type: 'corporate', company: form.company, token: data.token }));
      onClose();
      navigate(redirectTo);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth-callback';
    localStorage.setItem('cg-auth-redirect', redirectTo);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "w-full px-4 py-3 rounded-xl text-sm outline-none transition-all focus:ring-2 focus:ring-[#F78A7D]/30";
  const inputStyle = {
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    color: isDark ? '#FAF9F6' : '#1A2318',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="login-modal-overlay"
        >
          <div className="absolute inset-0" style={{
            backgroundColor: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
          }} />

          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 30 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="login-modal"
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-3xl"
            style={{
              backgroundColor: isDark ? 'rgba(24,30,22,0.92)' : 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(24px)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              boxShadow: isDark
                ? '0 32px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05) inset'
                : '0 32px 64px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.8) inset',
            }}
          >
            <button onClick={onClose} data-testid="login-modal-close"
              className="absolute top-4 right-4 z-10 p-2 rounded-full transition-all duration-200 hover:scale-110"
              style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }}>
              <X size={18} style={{ color: isDark ? '#FAF9F6' : '#1A2318' }} />
            </button>

            {/* Header */}
            <div className="relative px-8 pt-8 pb-3">
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #F78A7D, #6DAE65)' }} />
              <div className="flex items-center gap-3 mb-3">
                <img src={LOGO_URL} alt="Concept Gourmet" className="h-12 w-auto" />
              </div>
              <h2 className="text-2xl font-light tracking-tight" data-testid="login-modal-title"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: isDark ? '#FAF9F6' : '#1A2318' }}>
                {accountType === 'corporate'
                  ? <><span style={{ fontWeight: 600 }}>Compte Corporatif</span></>
                  : <>Bienvenue chez <span style={{ color: '#F78A7D', fontWeight: 600 }}>Concept Gourmet</span></>
                }
              </h2>
              <p className="text-sm mt-1" style={{ color: isDark ? 'rgba(250,249,246,0.5)' : 'rgba(26,35,24,0.5)' }}>
                {accountType === 'corporate' ? 'Facturation au nom de l\'entreprise' : 'Connectez-vous pour commander'}
              </p>
            </div>

            {/* Account type selector */}
            <div className="px-8 mb-5">
              <div className="flex gap-2">
                <button onClick={() => { setAccountType('individual'); setError(''); }}
                  data-testid="tab-particuliers"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-105 shadow-md"
                  style={{
                    backgroundColor: accountType === 'individual' ? '#F78A7D' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                    color: accountType === 'individual' ? '#fff' : (isDark ? 'rgba(250,249,246,0.5)' : 'rgba(26,35,24,0.4)'),
                    boxShadow: accountType === 'individual' ? '0 4px 16px rgba(247,138,125,0.4)' : 'none',
                    border: accountType === 'individual' ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                  }}>
                  <User size={16} /> Particuliers
                </button>
                <button onClick={() => { setAccountType('corporate'); setError(''); }}
                  data-testid="tab-corporatif"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-105 shadow-md"
                  style={{
                    backgroundColor: accountType === 'corporate' ? '#6DAE65' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                    color: accountType === 'corporate' ? '#fff' : (isDark ? 'rgba(250,249,246,0.5)' : 'rgba(26,35,24,0.4)'),
                    boxShadow: accountType === 'corporate' ? '0 4px 16px rgba(109,174,101,0.4)' : 'none',
                    border: accountType === 'corporate' ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                  }}>
                  <Building size={16} /> Corporatif
                </button>
              </div>
            </div>

            {/* ========== PARTICULIERS ========== */}
            {accountType === 'individual' && (
              <div>
                {/* Social Login */}
                <div className="px-8 space-y-2.5 mb-4">
                  <button onClick={handleGoogleLogin} data-testid="google-login-btn"
                    className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                      color: isDark ? '#FAF9F6' : '#1A2318',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}>
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continuer avec Google
                  </button>
                  <button onClick={handleGoogleLogin} data-testid="facebook-login-btn"
                    className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-white"
                    style={{ backgroundColor: '#1877F2', boxShadow: '0 2px 8px rgba(24,119,242,0.3)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Continuer avec Facebook
                  </button>
                </div>

                {/* Separator */}
                <div className="px-8 mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                    <span className="text-xs font-medium" style={{ color: isDark ? 'rgba(250,249,246,0.3)' : 'rgba(26,35,24,0.3)' }}>ou</span>
                    <div className="flex-1 h-px" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                  </div>
                </div>

                {/* Email/Password form */}
                <form onSubmit={handleIndividualSubmit} className="px-8 space-y-3" data-testid="login-form-individual">
                  <input type="email" placeholder="Courriel *" required value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    data-testid="modal-auth-email" className={inputCls} style={inputStyle} />

                  {!forgotMode && (
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} placeholder="Mot de passe *" required value={form.password}
                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                        data-testid="modal-auth-password" className={inputCls + ' pr-12'} style={inputStyle} />
                      <button type="button" onClick={() => setShowPassword(p => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors hover:bg-black/5"
                        data-testid="toggle-password">
                        {showPassword ? <EyeOff size={16} style={{ color: 'var(--color-muted-fg)' }} /> : <Eye size={16} style={{ color: 'var(--color-muted-fg)' }} />}
                      </button>
                    </div>
                  )}

                  {error && <p className="text-sm text-red-400 text-center" data-testid="modal-auth-error">{error}</p>}

                  {forgotSent && (
                    <p className="text-sm text-center" style={{ color: '#6DAE65' }}>
                      Si ce courriel existe, un lien de reinitialisation a ete envoye.
                    </p>
                  )}

                  {!forgotMode ? (
                    <>
                      <button type="submit" disabled={loading} data-testid="modal-auth-submit"
                        className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #2C3529 0%, #1A2318 100%)', boxShadow: '0 4px 20px rgba(26,35,24,0.3)' }}>
                        {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Se connecter'}
                      </button>
                      <button type="button" onClick={() => { setForgotMode(true); setError(''); }}
                        data-testid="forgot-password-btn"
                        className="w-full text-center text-xs font-medium py-1 transition-colors hover:underline"
                        style={{ color: 'var(--color-primary)' }}>
                        Mot de passe oublie ?
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" disabled={loading || !form.email}
                        onClick={() => { setForgotSent(true); setTimeout(() => setForgotMode(false), 3000); }}
                        data-testid="send-reset-btn"
                        className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' }}>
                        Envoyer le lien de reinitialisation
                      </button>
                      <button type="button" onClick={() => { setForgotMode(false); setError(''); }}
                        className="w-full text-center text-xs font-medium py-1 transition-colors hover:underline"
                        style={{ color: 'var(--color-muted-fg)' }}>
                        Retour a la connexion
                      </button>
                    </>
                  )}
                </form>
              </div>
            )}

            {/* ========== CORPORATIF ========== */}
            {accountType === 'corporate' && (
              <form onSubmit={handleCorpoSubmit} className="px-8 space-y-3" data-testid="login-form-corporate">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: isDark ? 'rgba(250,249,246,0.4)' : 'rgba(26,35,24,0.4)' }}>
                    Nom de l'entreprise *
                  </label>
                  <div className="relative">
                    <Building size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-muted-fg)' }} />
                    <input type="text" placeholder="Ma Compagnie Inc." required value={form.company}
                      onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                      data-testid="corpo-company" className={inputCls + ' pl-10'} style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: isDark ? 'rgba(250,249,246,0.4)' : 'rgba(26,35,24,0.4)' }}>
                    Personne qui commande *
                  </label>
                  <div className="relative">
                    <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-muted-fg)' }} />
                    <input type="text" placeholder="Jean Tremblay" required value={form.contact_name}
                      onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                      data-testid="corpo-contact" className={inputCls + ' pl-10'} style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: isDark ? 'rgba(250,249,246,0.4)' : 'rgba(26,35,24,0.4)' }}>
                    Courriel corporatif *
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-muted-fg)' }} />
                    <input type="email" placeholder="jean@compagnie.com" required value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      data-testid="corpo-email" className={inputCls + ' pl-10'} style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: isDark ? 'rgba(250,249,246,0.4)' : 'rgba(26,35,24,0.4)' }}>
                    Adresse de facturation *
                  </label>
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-muted-fg)' }} />
                    <input type="text" placeholder="123 rue Principale, Ville" required value={form.billing_address}
                      onChange={e => setForm(f => ({ ...f, billing_address: e.target.value }))}
                      data-testid="corpo-address" className={inputCls + ' pl-10'} style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: isDark ? 'rgba(250,249,246,0.4)' : 'rgba(26,35,24,0.4)' }}>
                    Telephone
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-muted-fg)' }} />
                    <input type="tel" placeholder="418 555-1234" value={form.phone}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        let fmt = digits;
                        if (digits.length > 3 && digits.length <= 6) fmt = `${digits.slice(0,3)} ${digits.slice(3)}`;
                        else if (digits.length > 6) fmt = `${digits.slice(0,3)} ${digits.slice(3,6)}-${digits.slice(6)}`;
                        setForm(f => ({ ...f, phone: fmt }));
                      }}
                      data-testid="corpo-phone" className={inputCls + ' pl-10'} style={inputStyle} />
                  </div>
                </div>

                {error && <p className="text-sm text-red-400 text-center" data-testid="modal-auth-error">{error}</p>}

                <button type="submit" disabled={loading} data-testid="corpo-submit"
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: '#1A1A1A', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>
                    <Building size={16} /> Creer / Se connecter
                  </>}
                </button>

                <p className="text-center text-xs pt-1 pb-2" style={{ color: '#6DAE65' }}>
                  Vos factures seront emises au nom de l'entreprise.
                </p>
              </form>
            )}

            {/* Quick access */}
            <div className="px-8 pt-5 pb-6 mt-3" style={{
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: isDark ? 'rgba(250,249,246,0.3)' : 'rgba(26,35,24,0.3)' }}>
                Acces rapide
              </p>
              <div className="space-y-2.5">
                <a href="https://ecodemo.dev/parent-login" target="_blank" rel="noopener noreferrer"
                  data-testid="modal-commande-scolaire"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-[1.02] shadow-md"
                  style={{
                    backgroundColor: '#6DAE65',
                    color: '#fff',
                    boxShadow: '0 4px 16px rgba(109,174,101,0.4)',
                  }}>
                  <GraduationCap size={18} />
                  Commande Scolaire
                </a>
                <a href="https://ecodemo.dev/garderie-login" target="_blank" rel="noopener noreferrer"
                  data-testid="modal-commande-garderie"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-[1.02] shadow-md"
                  style={{
                    backgroundColor: '#F78A7D',
                    color: '#fff',
                    boxShadow: '0 4px 16px rgba(247,138,125,0.4)',
                  }}>
                  <Baby size={18} />
                  Commande Garderie/CPE
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
